import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { ROLE_LABELS, type CollaboratorRole } from '../../services/auth/permissions';
import { Avatar } from '../ui/Avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export interface CollaboratorOption {
  id: string;
  full_name?: string | null;
  email?: string | null;
  role?: string | null;
  avatar_url?: string | null;
}

export function collaboratorDisplayName(
  c: Pick<CollaboratorOption, 'full_name' | 'email'>,
): string {
  const name = c.full_name?.trim();
  if (name) return name;
  const email = c.email?.trim();
  if (email) return email;
  return 'Collaborateur';
}

export function collaboratorRoleLabel(role?: string | null): string | null {
  if (!role) return null;
  return ROLE_LABELS[role as CollaboratorRole] ?? role;
}

export function CollaboratorOptionRow({
  option,
  selected = false,
  trailing,
  size = 'xs',
}: {
  option: CollaboratorOption;
  selected?: boolean;
  trailing?: React.ReactNode;
  size?: 'xs' | 'sm';
}) {
  const name = collaboratorDisplayName(option);
  const role = collaboratorRoleLabel(option.role);
  const subtitle = role ?? (option.full_name ? option.email : null) ?? null;

  return (
    <>
      <Avatar name={name} src={option.avatar_url} size={size} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-cime-text-primary truncate">{name}</p>
        {subtitle ? (
          <p className="text-[11px] text-cime-text-muted truncate">{subtitle}</p>
        ) : null}
      </div>
      {trailing}
      {selected && !trailing ? (
        <Check className="w-4 h-4 text-cime-primary shrink-0" aria-hidden />
      ) : null}
    </>
  );
}

interface CollaboratorSelectProps {
  options: CollaboratorOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
}

export function CollaboratorSelect({
  options,
  value,
  onChange,
  placeholder = '— Choisir —',
  disabled,
  className,
  id,
  emptyMessage = 'Aucun collaborateur trouvé',
  searchPlaceholder = 'Rechercher un collaborateur…',
}: CollaboratorSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const name = collaboratorDisplayName(o).toLowerCase();
      const email = (o.email ?? '').toLowerCase();
      const role = (collaboratorRoleLabel(o.role) ?? '').toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [options, query]);

  function handlePick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled || options.length === 0}
          className={cn(
            'flex w-full items-center gap-2 rounded-cime border border-neutral-300 bg-white px-3 py-2 text-left text-sm',
            'hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary/20 focus:border-transparent',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className,
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selected ? (
            <>
              <Avatar
                name={collaboratorDisplayName(selected)}
                src={selected.avatar_url}
                size="xs"
              />
              <span className="min-w-0 flex-1 truncate text-cime-text-primary">
                {collaboratorDisplayName(selected)}
              </span>
            </>
          ) : (
            <span className="flex-1 text-neutral-400">{placeholder}</span>
          )}
          <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="z-[80] w-[var(--radix-popover-trigger-width)] min-w-[16rem] p-0 bg-white border-neutral-200"
      >
        <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2">
          <Search className="w-4 h-4 text-neutral-400 shrink-0" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-400 text-cime-text-primary"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-cime-text-muted">
              {emptyMessage}
            </p>
          ) : (
            filtered.map((o) => {
              const isSelected = o.id === value;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handlePick(o.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                    isSelected ? 'bg-cime-primary/10' : 'hover:bg-neutral-50',
                  )}
                >
                  <CollaboratorOptionRow option={o} selected={isSelected} />
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
